import { useState, useEffect } from "react";
import { Select, Spin, DatePicker } from "antd";
import Columns from "../../columns/RVReport.js"
import LoadableButton from "../../components/buttons/LoadableButton.jsx";
import MyPagination from "../../components/global/pagination/Pagination.jsx";
import TableWithSkeleton from "../../components/global/table/TableWithSkeleton.jsx";
import { AdminGetRvTradesApi, GetAllTraderApi, GetTraderAccountsByIdApi } from "../../api/request/client";
import { mapRvTradeData } from './componets/mapRvTradeData.jsx';
const { RangePicker } = DatePicker;


function RVReport() {

    const [isSubmit, setIsSubmit] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [clientsLoading, setClientsLoading] = useState(false);
    const [accountsLoading, setAccountsLoading] = useState(false);
    const [selectedClient, setSelectedClient] = useState(null);
    const [selectedAccount, setSelectedAccount] = useState(null);
    const [summary, setSummary] = useState({});
    const [data, setData] = useState([]);
    const [dates, setDates] = useState([]);
    const [clients, setClients] = useState([]);
    const [accounts, setAccounts] = useState([]);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalRecords, setTotalRecords] = useState(0);

    const getClients = async (value) => {
        try {
            setClientsLoading(true);
            let params = {
                ApplyPagination: false,
                SearchText: value || '',
            };
            const { data } = await GetAllTraderApi(new URLSearchParams(params).toString());
            if (data?.success) {
                const formattedClients = data.data.map(client => ({
                    value: client.userId,
                    label: (
                        <div className="flex flex-col">
                            <span className="font-medium">{client.name}</span>
                            <span className="text-gray-500 text-sm">{client.email}</span>
                        </div>
                    ),
                    title: client.name,
                    email: client.email,
                    name: client.name
                }));
                setClients(formattedClients);
            }
            setClientsLoading(false);
        } catch (error) {
            setClientsLoading(false);
        }
    };

    const getAccounts = async (traderId) => {
        try {
            setAccountsLoading(true);
            setAccounts([]);
            const { data } = await GetTraderAccountsByIdApi(traderId, true);
            if (data?.success) {
                const formatted = data.data.map(acc => ({
                    value: acc.login,
                    label: (
                        <div className="flex flex-col">
                            <span className="font-medium">{acc.login}</span>
                            <span className="text-gray-500 text-sm">Balance: {acc.balance ?? 0}</span>
                        </div>
                    ),
                    title: acc.login,
                    login: acc.login,
                    balance: acc.balance,
                    raw: acc,
                }));
                setAccounts(formatted);
            }
            setAccountsLoading(false);
        } catch (error) {
            setAccountsLoading(false);
        }
    };

    function customDebounce(func, delay) {
        let timer;
        return function (...args) {
            clearTimeout(timer);
            timer = setTimeout(() => {
                func.apply(this, args);
            }, delay);
        };
    }

    const debouncedClientSearch = customDebounce((value) => {
        if (value.length >= 3) {
            getClients(value);
        }
    }, 500);

    const clientFilterOption = (input, option) => {
        const clientName = option?.name?.toString()?.toLowerCase() || "";
        const clientEmail = option?.email?.toString()?.toLowerCase() || "";
        const searchTerm = input.toLowerCase();
        return clientName.includes(searchTerm) || clientEmail.includes(searchTerm);
    };

    const accountFilterOption = (input, option) => {
        const login = option?.login?.toString() || "";
        const balance = option?.balance?.toString() || "";
        const searchTerm = input.toLowerCase();
        return login.includes(searchTerm) || balance.includes(searchTerm);
    };

    const handleClientChange = (value, option) => {
        setSelectedClient(option || null);
        setSelectedAccount(null);
        if (option && option.value) {
            getAccounts(option.value);
        } else {
            setAccounts([]);
        }
    };

    const handleAccountChange = (value, option) => {
        setSelectedAccount(option || null);
        setPage(1);
        setIsSubmit(false);
        setData([]);
        setSummary({
            "totalProfit": 0,
            "totalLoss": 0,
            "netProfit": 0,
        });
        setTotalRecords(0);
    };

    const getRvReport = async (traderId) => {
        try {
            setIsLoading(true);
            const params = {
                PageNumber: page,
                PageSize: pageSize,
                FromDate: dates.length === 2 ? dates[0].format('MM-DD-YYYY') : '',
                ToDate: dates.length === 2 ? dates[1].format('MM-DD-YYYY') : ''
            };
            const { data } = await AdminGetRvTradesApi(traderId, new URLSearchParams(params).toString());
            if (data?.success) {
                const mapped = mapRvTradeData(data?.data);
                setData(mapped?.tableData);
                setSummary(mapped?.summary);
                setTotalRecords(data?.totalRecords);
            }
        } catch (error) { }
        finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (isSubmit && selectedAccount && selectedAccount.login) {
            getRvReport(selectedAccount?.login);
        }
    }, [isSubmit, page, pageSize]);


    return (
        <>
            <div className="w-full flex gap-5 justify-between items-center mb-5">
                <div className="flex gap-5 items-center">
                    <div className="ant-select-selector-white w-64">
                        <Select
                            showSearch
                            placeholder="Search Client"
                            optionFilterProp="children"
                            value={selectedClient?.value}
                            onSearch={(value) => { if (value.length < 3) { setClients([]); return; } debouncedClientSearch(value); }}
                            onChange={handleClientChange}
                            filterOption={clientFilterOption}
                            loading={clientsLoading}
                            options={clients}
                            style={{ width: '100%' }}
                            size="large"
                            optionLabelProp="title"
                            listHeight={300}
                            suffixIcon={clientsLoading ? <Spin size="small" /> : undefined}
                        />
                    </div>

                    <div className="ant-select-selector-white  w-64">
                        <Select
                            showSearch
                            placeholder="Select a account"
                            optionFilterProp="children"
                            value={selectedAccount?.value}
                            onChange={handleAccountChange}
                            filterOption={accountFilterOption}
                            loading={accountsLoading}
                            options={accounts}
                            style={{ width: '100%' }}
                            size="large"
                            optionLabelProp="title"
                            listHeight={300}
                            disabled={!selectedClient}
                            notFoundContent={accountsLoading ? (<Spin size="small" />) :
                                (selectedClient && accounts.length === 0 ? (<div className="text-gray-500">No record found</div>) : null)
                            }
                            suffixIcon={accountsLoading ? <Spin size="small" /> : undefined}
                        />
                    </div>

                    <div className=" w-64 ">
                        <RangePicker
                            value={dates}
                            className="w-full p-2"
                            format="DD-MM-YYYY"
                            onChange={(vals) => { setDates(vals || []); setPage(1); setIsSubmit(false); }}

                        />
                    </div>
                    <div className="flex items-end">
                        <LoadableButton
                            lable="Submit"
                            isLoading={isLoading}
                            loadingLable="Submitting..."
                            className="bg-primary text-sm text-white font-semibold px-10 py-2 rounded-lg themeHover  duration-500"
                            onClick={() => { if (!selectedAccount?.login) return; setPage(1); setIsSubmit(true); }}
                            disabled={!selectedAccount?.login || isLoading}
                        />
                    </div>
                    {
                        data?.length > 0 &&
                        <div className="flex items-center gap-3 ">
                            <div className="text-lg text-[#2F2B3DCC] font-medium  block">
                                Total Profit: <span className={`font-bold ${summary?.totalProfit > 0 ? 'text-green-500' : 'text-red-500'}`}>{(summary?.totalProfit).toFixed(2)}</span>
                            </div>
                            <div className="text-lg text-[#2F2B3DCC] font-medium block">
                                Total Loss: <span className={`font-bold ${summary?.totalLoss > 0 ? 'text-red-500' : 'text-green-500'}`}>{(summary?.totalLoss).toFixed(2)}</span>
                            </div>
                            <div className="text-lg text-[#2F2B3DCC] font-medium block">
                                Net Profit: <span className={`font-bold ${summary?.netProfit > 0 ? 'text-green-500' : 'text-red-500'}`}>{(summary?.netProfit).toFixed(2)}</span>
                            </div>
                        </div>
                    }
                </div>
            </div>

            <div className="w-full mt-3">
                <div className="border border-gray-200 rounded-xl overflow-hidden bg-white">
                    <TableWithSkeleton
                        columns={Columns}
                        data={data}
                        loading={isLoading}
                        page={page}
                        pageSize={pageSize}
                        index={true}
                    />
                </div>
            </div>
            <div className="flex justify-end mt-4">
                <MyPagination total={totalRecords} pageSize={pageSize} setPageSize={setPageSize} page={page} setPage={setPage} />
            </div>
        </>
    );
}

export default RVReport